import { DataType } from "../../../tables"
import { normalize } from "../../../utils/typeUtils"
import { useFiltersHooks } from "../../../features/ui"
import { Roadmap } from "../state/types"
import { useDriverLoaders } from "../loaders/driverLoaders"
import { useVehicleLoaders } from "../loaders/vehicleLoaders"
import { firstItem } from "../../../utils/listUtils"
import { useBranchLoaders } from "../../manager"
import { useRoadmapLoaders } from "../loaders/roadmapLoaders"
import { PreparationStatus } from "../../common"
import { Address } from "../../../features/address"
import { deliveryOrderHelpers, useDeliveryHooks, useDeliveryLoaders, useDeliveryOrderLoaders } from "../../deliveries"

export const useRoadmapHooks = () => {
    const { isDeliveryCompletable } = deliveryOrderHelpers
    const { getDeliveryAddress } = useDeliveryHooks()
    const { search2 } = useFiltersHooks()
    
    const roadmapLoaders = useRoadmapLoaders()
    const driverLoaders = useDriverLoaders()
    const vehicleLoaders = useVehicleLoaders()
    const branchLoaders = useBranchLoaders()
    const deliveryLoaders = useDeliveryLoaders()
    const deliveryOrderLoaders = useDeliveryOrderLoaders()

    const getRouteData = async (roadmap: Roadmap) => {
        const { loadRoadmapDeliveriesLinks } = roadmapLoaders()
        const loadedDeliveryRoadmaps = await loadRoadmapDeliveriesLinks(roadmap.id)
        const deliveryIds = loadedDeliveryRoadmaps.map(deliveryRoadmap => deliveryRoadmap.deliveryId)
        const { loadDeliveries } = deliveryLoaders()
        const loadedDeliveries = await loadDeliveries(deliveryIds)
        const sortedDeliveries = loadedDeliveries.sort((delivery_A, delivery_B) => {
            const deliveryRoadmap_A = loadedDeliveryRoadmaps.find(deliveryRoadmap => deliveryRoadmap.deliveryId === delivery_A.id)!
            const deliveryRoadmap_B = loadedDeliveryRoadmaps.find(deliveryRoadmap => deliveryRoadmap.deliveryId === delivery_B.id)!
            return deliveryRoadmap_A.order - deliveryRoadmap_B.order
        })

        const loadedAddresses = await Promise.all(sortedDeliveries.map(delivery => getDeliveryAddress(delivery)))
        const { findBranch } = branchLoaders()
        const branch = findBranch(firstItem(sortedDeliveries)?.branchId)!
        const branchAddress = branch.address
        const routeAddressesData = new Map<string, Address>().set('start', branchAddress)
        for (const delivery of sortedDeliveries) {
            routeAddressesData.set(delivery.id, await getDeliveryAddress(delivery))
        }
        routeAddressesData.set('end', branchAddress)

        return {
            shipments: sortedDeliveries,
            shipmentAddresses: loadedAddresses,
            routeAddressesData
        }
    }

    const isRoadmapCompletable = async (roadmap: Roadmap): Promise<boolean> => {
        const { loadRoadmapDeliveriesLinks } = roadmapLoaders()
        const loadedDeliveryRoadmaps = await loadRoadmapDeliveriesLinks(roadmap.id)
        const { loadDeliveries } = deliveryLoaders()
        const loadedDeliveries = await loadDeliveries(loadedDeliveryRoadmaps.map(deliveryRoadmap => deliveryRoadmap.deliveryId))
        const { loadDeliveryOrders } = deliveryOrderLoaders()
        const loadedDeliveryOrders = await loadDeliveryOrders(loadedDeliveries.map(delivery => delivery.orderId!))
        return loadedDeliveryOrders.some(isDeliveryCompletable)
    }

    const searchRoadmaps = (
        roadmaps: Roadmap[],
        searchText = '',
        dateFrom?: Date,
        dateTo?: Date,
        preparationStatus?: PreparationStatus,    
        driverId?: string,
        vehicleId?: string
    ): Promise<Roadmap[]> => {
        const valuesFn = async (roadmapDT: DataType) => {
            const roadmap = roadmapDT as Roadmap
            const { findDriver } = driverLoaders()
            const { findVehicle } = vehicleLoaders()
            const driver = findDriver(roadmap.driverId)!
            const vehicle = findVehicle(roadmap.vehicleId)!
            return [
                roadmap.code.toString(),
                normalize(driver.name),
                driver.identifier.toString(),
                normalize(vehicle.name),
                normalize(vehicle.plate)
            ]
        }
        
        const filterFn = (roadmapDT: DataType) => {
            const roadmap = roadmapDT as Roadmap
            return (!dateFrom || new Date(roadmap.date) >= dateFrom) &&
                (!dateTo || new Date(roadmap.date) <= dateTo) &&    
                (!preparationStatus || roadmap.preparationStatus === preparationStatus) &&
                (!driverId || roadmap.driverId === driverId) &&
                (!vehicleId || roadmap.vehicleId === vehicleId)
        }

        const sortFn = (roadmapDT_A: DataType, roadmapDT_B: DataType) => 
            (roadmapDT_B as Roadmap).date.localeCompare((roadmapDT_A as Roadmap).date)

        return search2(roadmaps, searchText, valuesFn, filterFn, sortFn) as Promise<Roadmap[]>
    }

    return {
        getRouteData,
        isRoadmapCompletable,
        searchRoadmaps
    }
}
